import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { DURATION, enter } from '@/ui/motion/motion';

/**
 * How the screen changed:
 * - `forward`: deeper (a list to a case, a case to its Lab Notes); the new
 *   page comes in from the right;
 * - `back`: the reverse, from the left;
 * - `switch`: another section from the menu or the tabs, a quick cross-fade so
 *   it feels instant;
 * - `none`: nothing to show, such as the first screen after opening the app.
 */
export type RouteDirection = 'forward' | 'back' | 'switch' | 'none';

/** Where each history entry was scrolled to, so Back returns to the same place. */
const scrolled = new Map<string, number>();

function scrollerOf(element: HTMLElement | null): HTMLElement | null {
  let node = element?.parentElement ?? null;
  while (node !== null) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * The content area's page change. Request of 5 October 2026, section 3.
 *
 * Only this wrapper moves: the header and the bottom tabs stay put. The new
 * screen is drawn at once and then eases in, so nothing waits for the
 * animation and a tap during it lands. The browser's own View Transitions
 * need the router to start the change, which the router this app uses does
 * not offer, so the arrival is played with the Web Animations API instead; it
 * runs everywhere the app does.
 */
export function RouteTransition({
  transitionKey,
  scrollKey,
  direction,
  children,
}: {
  /** Changes when the screen does. */
  transitionKey: string;
  /** The history entry, for remembering how far it was scrolled. */
  scrollKey: string;
  direction: RouteDirection;
  children: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const element = box.current;
    const scroller = scrollerOf(element);
    // Back to where this entry was; anywhere new starts at the top.
    if (scroller !== null)
      scroller.scrollTop = direction === 'back' ? (scrolled.get(scrollKey) ?? 0) : 0;
    if (direction !== 'none') {
      enter(
        element,
        direction === 'forward' ? 'fromRight' : direction === 'back' ? 'fromLeft' : 'fade',
        { duration: direction === 'switch' ? DURATION.fast : DURATION.normal },
      );
    }
    return () => {
      // The entry being left: remember its place before the next screen draws.
      if (scroller !== null) scrolled.set(scrollKey, scroller.scrollTop);
    };
    // The screen changing is the only trigger; the direction belongs to it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitionKey]);

  return <div ref={box}>{children}</div>;
}
